import _ from "lodash";
import React from "react";
import styled from "styled-components";
import withProviders from "../../hooks/withProviders";
import UserDelegatesSelect from "../../components/forms/selects/UserDelegatesSelect";
import { fromNString, toNString } from "../../helpers/guids";
import { ThemedIcon, ThemedParagraph } from "../../components/utilities";
import { AdviserSummaryDashboardPageSubHeader } from "../../components/utilities/PageSubHeader";
import { FilterProvider, useFilterContext } from "../../hooks/FilterContext";
import { AdviserSummaryProvider, useAdviserSummaryProps } from "./hooks/AdviserSummaryContext";
import { PendingBusinessSheetsWidget } from "./components/PendingBusinessSheetsWidget";
import { MeetingsSummaryWidget } from "./components/MeetingsWidget";
import { PendingBusinessSheetProvider } from "./hooks/PendingBusinessSheetsContext";
import { MeetingsSummaryProvider } from "./hooks/MeetingsSummaryContext";
import { ReportsSummaryProvider } from "./hooks/ReportsSummaryContext";
import { ReportsWidget } from "./components/ReportsWidget";
import { ReviewsSummaryProvider } from "./hooks/ReviewsSummaryContext";
import { ReviewsWidget } from "./components/ReviewsWidget";
import { AssetsSummaryProvider } from "./hooks/AssetsSummaryContext";
import { AssetsWidget } from "./components/AssetsWidget";
import { FeesSummaryProvider } from "./hooks/FeesSummaryContext";
import { FeesWidget } from "./components/FeesWidget";
import { PendingBusinessSheetGroupingProvider } from "./hooks/PendingBusinessSheetGroupingContext";

const AdviserDashboardFiltersAppliedWarningMessage = styled(ThemedParagraph)`
    font-size: 12px !important;
`;

const AdviserSummary = () => {
    const { isDashboardAdmin, userId } = useAdviserSummaryProps();
    const { clearFilter, filter, isFiltersApplied, isReady, setFilter } = useFilterContext();
    const [internalSelection, setInternalSelection] = React.useState([]);

    const reportAuthorId = React.useMemo(() => {
        if (_.isEmpty(internalSelection) || internalSelection.length > 1) {
            return null;
        }
        return internalSelection[0];
    }, [ internalSelection ]);

    const onDelegateFilterChanged = (opts) => {
        setFilter(() => ({
            ...filter,
            userIds: opts.map(e => fromNString(e))
        }));
    };

    React.useEffect(() => {
        if (_.isEmpty(filter)) {
            return;
        }
        if (isReady) {
            setInternalSelection(() => filter.userIds.map(e => toNString(e)));
        }
    }, [ filter, isReady ]);

    // this is so the filter store has time to get the default filters
    // otherwise it returns a blank object because its not had time to update yet...
    if (!isReady) {
        return null;
    }

    return (
        <>
            <AdviserSummaryDashboardPageSubHeader>
                <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                        {isFiltersApplied && (
                            <React.Fragment>
                                <ThemedIcon icon="fa-triangle-exclamation" size="2x" variant="warning" />
                                <AdviserDashboardFiltersAppliedWarningMessage className="ms-2 text-muted">
                                    Warning - Filters are currently being applied to your results.
                                    <br />
                                    <a href="#" onClick={clearFilter}>Click here</a> to clear your filters.
                                </AdviserDashboardFiltersAppliedWarningMessage>
                            </React.Fragment>
                        )}
                    </div>
                    <div className="d-flex flex-fill justify-content-end align-items-center gap-2">
                        <div className="w-50">
                            <UserDelegatesSelect 
                                accessType={10}
                                disableAnimations={true}
                                isMulti={true}
                                isClearable={isDashboardAdmin}
                                defaultValue={internalSelection}
                                onMenuClose={() => onDelegateFilterChanged(internalSelection)}
                                onChange={(opts) => {
                                    if ((!opts || opts.length === 0) && !isDashboardAdmin) {
                                        setInternalSelection(() => [ toNString(userId) ]);
                                        return;
                                    }
                                    setInternalSelection(() => opts.map(el => el.value))
                                }}
                            />
                        </div>
                    </div>
                </div>
            </AdviserSummaryDashboardPageSubHeader>
            <section className="px-2 my-2 h-100">
                <div className="row g-2">
                    <PendingBusinessSheetProvider filter={filter} isReady={isReady}>
                        <PendingBusinessSheetGroupingProvider filter={filter} isReady={isReady}>
                            <PendingBusinessSheetsWidget />
                        </PendingBusinessSheetGroupingProvider>
                    </PendingBusinessSheetProvider>
                    <MeetingsSummaryProvider filter={filter} isReady={isReady}>
                        <MeetingsSummaryWidget />
                    </MeetingsSummaryProvider>
                    <ReportsSummaryProvider filter={filter} isReady={isReady}>
                        <ReportsWidget authorId={reportAuthorId} />
                    </ReportsSummaryProvider>
                    <ReviewsSummaryProvider filter={filter} isReady={isReady}>
                        <ReviewsWidget />
                    </ReviewsSummaryProvider>
                    <AssetsSummaryProvider filter={filter} isReady={isReady}>
                        <AssetsWidget />
                    </AssetsSummaryProvider>
                    <FeesSummaryProvider filter={filter} isReady={isReady}>
                        <FeesWidget />
                    </FeesSummaryProvider> 
                </div>
            </section>
        </>
    )
}

export {
    AdviserSummaryProvider
};

export default withProviders(({ children }) => {
    const { isDashboardAdmin, userId } = useAdviserSummaryProps();
    const [defaultFilters, setDefaultFilters] = React.useState(null);

    React.useEffect(() => {
        setDefaultFilters(() => isDashboardAdmin
            ? { userIds: [] }
            : { userIds: [ fromNString(userId) ] }
        )
    }, []);

    // filterStore="personal-summary"
    return (
        <FilterProvider defaultFilters={defaultFilters}>
            {children}
        </FilterProvider>
    );
})(AdviserSummary);